import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';

const schema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  subject: z.string().min(3, 'Subject must be at least 3 characters'),
  message: z.string().min(10, 'Message must be at least 10 characters'),
});

const ContactModal = ({ data }) => {
  const { language, t } = useLanguage();
  const { isDark } = useTheme();
  const isRtl = language === 'ar';
  const tab = data?.tab || 'email';

  const [submitState, setSubmitState] = useState('idle'); // idle | sending | success | error

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (formData) => {
    setSubmitState('sending');
    try {
      await axios.post('/api/contact', formData);
      setSubmitState('success');
      reset();
    } catch {
      setSubmitState('error');
    }
  };

  const phoneNumber = t('contact.phone.number');
  const phoneHref = `tel:${phoneNumber.replace(/[^+0-9]/g, '')}`;
  const phoneTitle = t('contact.phone.title');
  const officeTitle = t('contact.office.title');
  const officeAddress = t('contact.office.address');
  const officeCity = t('contact.office.city');
  const hoursTitle = t('contact.hours.title');
  const weekdays = t('contact.hours.weekdays');
  const weekend = t('contact.hours.weekend');
  const mapQuery = [officeAddress, officeCity].filter(Boolean).join(', ');
  const mapHref = `https://maps.google.com/?q=${encodeURIComponent(mapQuery)}`;

  const labels = {
    en: {
      emailTitle: 'Send Us a Message',
      emailSubtitle: "Fill in the form and we'll get back to you within one business day.",
      name: 'Your Name',
      email: 'Email Address',
      subject: 'Subject',
      message: 'Message',
      namePlaceholder: 'John Smith',
      emailPlaceholder: 'john@example.com',
      subjectPlaceholder: 'How can we help?',
      messagePlaceholder: 'Tell us about your inquiry...',
      send: 'Send Message',
      sending: 'Sending…',
      successTitle: 'Message Sent!',
      successMsg: 'Thank you for reaching out. Our team will contact you within one business day.',
      errorMsg: 'Something went wrong. Please try again or call us directly.',
      sendAnother: 'Send Another Message',
      phoneTitle: 'Telephone',
      phoneSubtitle: 'Our team is ready to assist you during business hours.',
      hours: 'Business Hours',
      weekdays: 'Sunday – Thursday: 9am – 7pm',
      weekend: 'Friday – Saturday: 10am – 4pm',
      callNow: 'Call Now',
      mainLine: 'Main Line',
      mapTitle: 'Our Location',
      mapSubtitle: 'Visit our headquarters in the heart of Dubai.',
      address: 'Royal Tower, Business Bay District',
      city: 'Dubai, United Arab Emirates',
      viewMap: 'View on Google Maps',
      hoursTitle: 'Office Hours',
      hoursSubtitle: 'We are available to assist you during the following hours.',
      note: 'For urgent inquiries outside business hours, please email us and we will respond as soon as possible.',
    },
    ar: {
      emailTitle: 'أرسل لنا رسالة',
      emailSubtitle: 'أكمل النموذج وسنتواصل معك خلال يوم عمل واحد.',
      name: 'اسمك الكامل',
      email: 'البريد الإلكتروني',
      subject: 'الموضوع',
      message: 'الرسالة',
      namePlaceholder: 'محمد العلي',
      emailPlaceholder: 'example@email.com',
      subjectPlaceholder: 'كيف يمكننا مساعدتك؟',
      messagePlaceholder: 'أخبرنا عن استفسارك...',
      send: 'إرسال الرسالة',
      sending: 'جارٍ الإرسال…',
      successTitle: 'تم إرسال الرسالة!',
      successMsg: 'شكراً لتواصلك معنا. سيتصل بك فريقنا خلال يوم عمل واحد.',
      errorMsg: 'حدث خطأ ما. يرجى المحاولة مرة أخرى أو الاتصال بنا مباشرة.',
      sendAnother: 'إرسال رسالة أخرى',
      phoneTitle: 'الهاتف',
      phoneSubtitle: 'فريقنا جاهز لمساعدتك خلال ساعات العمل.',
      hours: 'ساعات العمل',
      weekdays: 'الأحد – الخميس: ٩ص – ٧م',
      weekend: 'الجمعة – السبت: ١٠ص – ٤م',
      callNow: 'اتصل الآن',
      mainLine: 'الخط الرئيسي',
      mapTitle: 'موقعنا',
      mapSubtitle: 'زر مقرنا الرئيسي في قلب دبي.',
      address: 'برج رويال، منطقة الخليج التجاري',
      city: 'دبي، الإمارات العربية المتحدة',
      viewMap: 'عرض على خرائط جوجل',
      hoursTitle: 'ساعات المكتب',
      hoursSubtitle: 'نحن متاحون لمساعدتك خلال الساعات التالية.',
      note: 'للاستفسارات العاجلة خارج ساعات العمل، يرجى مراسلتنا بالبريد الإلكتروني وسنرد في أقرب وقت ممكن.',
    },
  };

  const l = labels[language];

  const inputClass = `w-full px-4 py-3 rounded-sm text-sm transition-all outline-none border focus:border-[var(--gold-primary)] ${
    isDark
      ? 'bg-white/5 border-white/10 text-white placeholder-white/30 focus:bg-white/8'
      : 'bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400 focus:bg-white'
  }`;

  const errorClass = `text-xs mt-1 text-red-400 ${isRtl ? 'text-right' : 'text-left'}`;

  // EMAIL / CONTACT FORM tab
  if (tab === 'email') {
    return (
      <div className={`p-6 sm:p-8 ${isRtl ? 'text-right' : 'text-left'}`}>
        <div className={`flex items-center gap-3 mb-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
          <div className={`w-10 h-10 flex items-center justify-center rounded-sm ${isDark ? 'bg-[var(--gold-primary)]/20' : 'bg-[var(--gold-primary)]/10'}`}>
            <Mail className="w-5 h-5 text-[var(--gold-primary)]" />
          </div>
          <h2 className={`font-display text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{l.emailTitle}</h2>
        </div>
        <p className={`text-sm mb-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{l.emailSubtitle}</p>

        {submitState === 'success' ? (
          <div className={`text-center py-10 ${isDark ? 'bg-[var(--gold-primary)]/10' : 'bg-green-50'} rounded-sm`}>
            <CheckCircle className="w-12 h-12 text-[var(--gold-primary)] mx-auto mb-4" />
            <h3 className={`font-display text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>{l.successTitle}</h3>
            <p className={`text-sm mb-6 max-w-sm mx-auto ${isDark ? 'text-white/70' : 'text-gray-600'}`}>{l.successMsg}</p>
            <button
              onClick={() => setSubmitState('idle')}
              className="px-5 py-2.5 bg-[var(--gold-primary)] text-black font-semibold text-sm rounded-sm hover:bg-[var(--gold-hover)] transition-colors"
            >
              {l.sendAnother}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={`block text-xs uppercase tracking-wider font-semibold mb-1.5 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{l.name}</label>
                <input {...register('name')} dir={isRtl ? 'rtl' : 'ltr'} placeholder={l.namePlaceholder} className={`${inputClass} ${isRtl ? 'text-right' : 'text-left'}`} />
                {errors.name && <p className={errorClass}>{errors.name.message}</p>}
              </div>
              <div>
                <label className={`block text-xs uppercase tracking-wider font-semibold mb-1.5 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{l.email}</label>
                <input {...register('email')} type="email" dir="ltr" placeholder={l.emailPlaceholder} className={`${inputClass} text-left`} />
                {errors.email && <p className={errorClass}>{errors.email.message}</p>}
              </div>
            </div>
            <div className="mb-4">
              <label className={`block text-xs uppercase tracking-wider font-semibold mb-1.5 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{l.subject}</label>
              <input {...register('subject')} dir={isRtl ? 'rtl' : 'ltr'} placeholder={l.subjectPlaceholder} className={`${inputClass} ${isRtl ? 'text-right' : 'text-left'}`} />
              {errors.subject && <p className={errorClass}>{errors.subject.message}</p>}
            </div>
            <div className="mb-5">
              <label className={`block text-xs uppercase tracking-wider font-semibold mb-1.5 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{l.message}</label>
              <textarea {...register('message')} dir={isRtl ? 'rtl' : 'ltr'} rows={5} placeholder={l.messagePlaceholder} className={`${inputClass} resize-none ${isRtl ? 'text-right' : 'text-left'}`} />
              {errors.message && <p className={errorClass}>{errors.message.message}</p>}
            </div>
            {submitState === 'error' && (
              <div className={`flex items-center gap-2 text-sm text-red-400 mb-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {l.errorMsg}
              </div>
            )}
            <button
              type="submit"
              disabled={submitState === 'sending'}
              className={`btn-shine inline-flex items-center gap-2 px-7 py-3 bg-[var(--gold-primary)] text-black font-semibold text-sm uppercase tracking-wider rounded-sm hover:bg-[var(--gold-hover)] transition-all disabled:opacity-60 ${isRtl ? 'flex-row-reverse' : ''}`}
            >
              <Send className="w-4 h-4" />
              {submitState === 'sending' ? l.sending : l.send}
            </button>
          </form>
        )}
      </div>
    );
  }

  // PHONE tab
  if (tab === 'phone') {
    return (
      <div className={`p-6 sm:p-8 ${isRtl ? 'text-right' : 'text-left'}`}>
        <div className={`flex items-center gap-3 mb-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
          <div className={`w-10 h-10 flex items-center justify-center rounded-sm ${isDark ? 'bg-[var(--gold-primary)]/20' : 'bg-[var(--gold-primary)]/10'}`}>
            <Phone className="w-5 h-5 text-[var(--gold-primary)]" />
          </div>
          <h2 className={`font-display text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{phoneTitle}</h2>
        </div>
        <p className={`text-sm mb-8 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{l.phoneSubtitle}</p>

        <div className={`mb-8 p-6 rounded-sm ${isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200'}`}>
          <span className={`text-xs uppercase tracking-widest ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{l.mainLine}</span>
          <p className="font-display text-3xl sm:text-4xl font-bold gold-text mt-1 mb-5" dir="ltr">{phoneNumber}</p>
          <a
            href={phoneHref}
            className={`btn-shine inline-flex items-center gap-2 px-6 py-3 bg-[var(--gold-primary)] text-black font-semibold text-sm uppercase tracking-wider rounded-sm hover:bg-[var(--gold-hover)] transition-all ${isRtl ? 'flex-row-reverse' : ''}`}
          >
            <Phone className="w-4 h-4" />
            {l.callNow}
          </a>
        </div>

        <div>
          <h4 className={`text-xs uppercase tracking-widest font-semibold text-[var(--gold-primary)] mb-4 flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <Clock className="w-4 h-4" />
            {hoursTitle}
          </h4>
          <div className="space-y-3">
            {[weekdays, weekend].map((line, i) => (
              <div key={i} className={`flex items-center gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold-primary)] flex-shrink-0" />
                <span className={`text-sm ${isDark ? 'text-white/75' : 'text-gray-700'}`}>{line}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // OFFICE / MAP tab
  if (tab === 'office') {
    return (
      <div className={`p-6 sm:p-8 ${isRtl ? 'text-right' : 'text-left'}`}>
        <div className={`flex items-center gap-3 mb-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
          <div className={`w-10 h-10 flex items-center justify-center rounded-sm ${isDark ? 'bg-[var(--gold-primary)]/20' : 'bg-[var(--gold-primary)]/10'}`}>
            <MapPin className="w-5 h-5 text-[var(--gold-primary)]" />
          </div>
          <h2 className={`font-display text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{officeTitle}</h2>
        </div>
        <p className={`text-sm mb-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{l.mapSubtitle}</p>

        {/* Map Image */}
        <div className={`relative h-48 sm:h-64 rounded-sm overflow-hidden mb-6 ${isDark ? 'bg-[#1A1A1A]' : 'bg-gray-100'}`}>
          <img
            src="/uploads/9a42e3f1-cacf-4315-89d5-58f9a9d4bdcb.png"
            alt={officeCity}
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className={`text-center p-6 rounded-sm ${isDark ? 'bg-[#111111]/90' : 'bg-white/90'} shadow-xl`}>
              <MapPin className="w-8 h-8 text-[var(--gold-primary)] mx-auto mb-2" />
              <p className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>{officeAddress}</p>
              <p className={`text-xs mt-1 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{officeCity}</p>
            </div>
          </div>
        </div>

        <a
          href={mapHref}
          target="_blank"
          rel="noopener noreferrer"
          className={`btn-shine inline-flex items-center gap-2 px-6 py-3 bg-[var(--gold-primary)] text-black font-semibold text-sm uppercase tracking-wider rounded-sm hover:bg-[var(--gold-hover)] transition-all ${isRtl ? 'flex-row-reverse' : ''}`}
        >
          <ExternalLink className="w-4 h-4" />
          {l.viewMap}
        </a>
      </div>
    );
  }

  // HOURS tab
  return (
    <div className={`p-6 sm:p-8 ${isRtl ? 'text-right' : 'text-left'}`}>
      <div className={`flex items-center gap-3 mb-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
        <div className={`w-10 h-10 flex items-center justify-center rounded-sm ${isDark ? 'bg-[var(--gold-primary)]/20' : 'bg-[var(--gold-primary)]/10'}`}>
          <Clock className="w-5 h-5 text-[var(--gold-primary)]" />
        </div>
        <h2 className={`font-display text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{hoursTitle}</h2>
      </div>
      <p className={`text-sm mb-8 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{l.hoursSubtitle}</p>

      <div className="space-y-4 mb-8">
        {[weekdays, weekend].map((line, i) => (
          <div
            key={i}
            className={`flex items-center p-4 rounded-sm ${isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200'} ${isRtl ? 'flex-row-reverse text-right' : 'text-left'}`}
          >
            <span className={`text-sm font-medium ${isDark ? 'text-white/80' : 'text-gray-700'}`}>{line}</span>
          </div>
        ))}
      </div>

      <p className={`text-xs leading-relaxed ${isDark ? 'text-white/50' : 'text-gray-400'}`}>{l.note}</p>
    </div>
  );
};

export default ContactModal;
